import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Paper, Tabs, Tab, Button, Alert } from '@mui/material';
import { 
  History as HistoryIcon,
  AccountBalance as HMRCIcon,
  CheckCircle,
  LinkOff
} from '@mui/icons-material';
import VatMonitoring from './VatMonitoring';
import VatLiabilities from './VatLiabilities';
import VatSubmissionHistory from './VatSubmissionHistory';
import TaxAccrual from './TaxAccrual';
import Drawings from './Drawings';
import api from './axiosConfig';

// Set to true once HMRC has approved SalonLogic for live submissions.
// While false, a notice tells users that connecting is for testing only.
const HMRC_LIVE = false;

const SCHEME_LABELS = {
  STANDARD_RATE: 'Standard VAT accounting',
  CASH_ACCOUNTING: 'Cash accounting',
  FLAT_RATE: 'Flat Rate Scheme',
  FIRST_YEAR_FLAT_RATE: 'Flat Rate Scheme (first-year rate)',
};

const formatSchemeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const VatDashboard = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(0);
  const [hmrcStatus, setHmrcStatus] = React.useState(null);
  const [vatScheme, setVatScheme] = React.useState(null);
  
  React.useEffect(() => {
    // Fetch HMRC connection status
    const fetchHmrcStatus = async () => {
      try {
        const res = await api.get('/hmrc/status');
        setHmrcStatus(res.data);
      } catch (err) {
        console.error('Failed to fetch HMRC status', err);
        setHmrcStatus({ connected: false });
      }
    };
    fetchHmrcStatus();

    const fetchVatScheme = async () => {
      try {
        const res = await api.get('/company/vat-scheme');
        setVatScheme(res.data);
      } catch (err) {
        console.error('Failed to fetch VAT scheme', err);
      }
    };
    fetchVatScheme();
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Finance Dashboard</Typography>
        <Button 
          component={Link} 
          to="/audit-trail"
          startIcon={<HistoryIcon />}
          variant="outlined"
          size="small"
        >
          View Audit Trail
        </Button>
      </Box>
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="VAT" />
        <Tab label="Tax Accruals" />
        <Tab label="Drawings" />
      </Tabs>
      
      {/* VAT Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* VAT scheme in force */}
          {vatScheme?.is_vat_registered && (
            <Grid item xs={12}>
              <Alert severity="info" icon={false}>
                <strong>VAT scheme: {SCHEME_LABELS[vatScheme.scheme] || vatScheme.scheme || 'Not set'}</strong>
                {vatScheme.effective_from && ` — since ${formatSchemeDate(vatScheme.effective_from)}`}
                {vatScheme.scheme === 'CASH_ACCOUNTING' &&
                  '. VAT is accounted for when money is received or paid, not on the invoice date.'}
                {' '}You can change this in Company Settings.
              </Alert>
            </Grid>
          )}
          {/* HMRC MTD Connection Card */}
          <Grid item xs={12}>
            <Paper 
              sx={{ 
                p: 3, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                bgcolor: hmrcStatus?.connected ? '#f0fdf4' : '#fef2f2',
                border: '1px solid',
                borderColor: hmrcStatus?.connected ? '#bbf7d0' : '#fecaca'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: 2, 
                  bgcolor: hmrcStatus?.connected ? '#dcfce7' : '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <HMRCIcon sx={{ color: hmrcStatus?.connected ? '#16a34a' : '#dc2626' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    HMRC Making Tax Digital
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {hmrcStatus?.connected ? (
                      <>
                        <CheckCircle sx={{ fontSize: 16, color: '#16a34a' }} />
                        <Typography variant="body2" color="text.secondary">
                          Connected — VRN: {hmrcStatus.vrn}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <LinkOff sx={{ fontSize: 16, color: '#dc2626' }} />
                        <Typography variant="body2" color="text.secondary">
                          Not connected — Connect to submit VAT returns directly
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
              <Button
                variant={hmrcStatus?.connected ? "outlined" : "contained"}
                startIcon={<HMRCIcon />}
                onClick={() => navigate('/settings/hmrc')}
                sx={hmrcStatus?.connected ? {} : {
                  background: 'linear-gradient(135deg, #1e3a5f, #0d1b2a)',
                }}
              >
                {hmrcStatus?.connected ? 'Manage Connection' : 'Connect to HMRC'}
              </Button>
            </Paper>
          </Grid>

          {!HMRC_LIVE && (
            <Grid item xs={12}>
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/reports/transaction')}>
                    Go to export
                  </Button>
                }
              >
                <strong>Direct submission to HMRC isn't live yet.</strong> Connecting is for testing only,
                and nothing you submit here counts as a real VAT return. To send your figures to your accountant,
                use the accountant export on Reports, then Transactions.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <VatSubmissionHistory />
          </Grid>

          <Grid item xs={12}>
            <VatMonitoring />
          </Grid>
          
          <Grid item xs={12}>
            <VatLiabilities />
          </Grid>
        </Grid>
      )}
      
      {/* Tax Accruals Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TaxAccrual />
          </Grid>
        </Grid>
      )}
      
      {/* Drawings Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Drawings />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default VatDashboard;